import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  ClockIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  ListBulletIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { KanbanService, formatKanbanData } from '../../services/KanbanService';
import { ProjectService } from '../../services/ProjectService';
import { TaskService } from '../../services/TaskService';
import { getStatusLabel, getStatusColorClasses } from '../../utils/statusConfig';
import CreateProjectModal from '../projects/CreateProjectModal';
import EditProjectModal from '../projects/EditProjectModal';
import ProjectTasksModal from '../projects/ProjectTasksModal';
import toast from 'react-hot-toast';

const KanbanView = () => {
  const [kanbanData, setKanbanData] = useState({
    pending: [],
    progress: [],
    completed: []
  });
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasksRefreshSignal, setTasksRefreshSignal] = useState(0);
  const [completionModal, setCompletionModal] = useState({
    isOpen: false,
    project: null,
    sourceColumn: null,
    destColumn: null,
    sourceIndex: null,
    destinationIndex: null,
    incompleteTasksCount: 0
  });
  const [completionProcessing, setCompletionProcessing] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    project: null
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadKanbanData();
  }, []);

  const loadKanbanData = async () => {
    setLoading(true);
    try {
      const result = await KanbanService.getKanbanData();
      
      if (result.success) {
        const formattedData = formatKanbanData(result.data);
        setKanbanData(formattedData);
      } else {
        console.error('Error al cargar datos del kanban:', result.error);
        toast.error('Error al cargar el tablero Kanban');
      }
    } catch (error) {
      console.error('Error al cargar datos del kanban:', error);
      toast.error('Error al cargar el tablero Kanban');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar clic en tarjeta de proyecto (ver tareas)
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsTasksModalOpen(true);
  };

  // Función para manejar edición de proyecto
  const handleEditProject = (project, e) => {
    e.stopPropagation(); // Evitar que se abra el modal de tareas
    setSelectedProject(project);
    setIsEditModalOpen(true);
  };

  // Función para eliminar proyecto
  const handleDeleteProject = (project, e) => {
    e.stopPropagation(); // Evitar que se abra el modal de tareas
    
    // Abrir modal de confirmación
    setDeleteModal({
      isOpen: true,
      project: project
    });
  };

  // Función para confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!deleteModal.project || deleting) return;

    setDeleting(true);
    const loadingToast = toast.loading('Eliminando proyecto...');
    
    try {
      const result = await ProjectService.deleteProject(deleteModal.project.id);
      
      if (result.success) {
        toast.success('Proyecto eliminado exitosamente', { id: loadingToast });
        // Cerrar modal
        setDeleteModal({ isOpen: false, project: null });
        // Recargar datos del kanban
        loadKanbanData();
      } else {
        toast.error(result.error || 'Error al eliminar el proyecto', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error al eliminar proyecto:', error);
      toast.error('Error al eliminar el proyecto', { id: loadingToast });
    } finally {
      setDeleting(false);
    }
  };

  // Función para cancelar eliminación
  const handleCancelDelete = () => {
    if (!deleting) {
      setDeleteModal({ isOpen: false, project: null });
    }
  };

  // Función para refrescar proyectos después de editar o crear
  const handleProjectUpdated = () => {
    loadKanbanData();
  };

  const handleProjectCreated = () => {
    loadKanbanData();
  };

  // Manejador de drag and drop
  const resetCompletionModal = () => {
    setCompletionModal({
      isOpen: false,
      project: null,
      sourceColumn: null,
      destColumn: null,
      sourceIndex: null,
      destinationIndex: null,
      incompleteTasksCount: 0
    });
    setCompletionProcessing(false);
  };

  const getTagChipStyles = (color) => {
    const hex = color?.startsWith('#') ? color : `#${color || '4B5563'}`;
    return {
      backgroundColor: `${hex}20`,
      color: hex
    };
  };

  const refreshProjectTasks = async (projectId) => {
    try {
      await TaskService.getProjectTasks(projectId);
    } catch (error) {
      console.error('Error al refrescar tareas del proyecto:', error);
    } finally {
      if (isTasksModalOpen && selectedProject?.id === projectId) {
        setTasksRefreshSignal(Date.now());
      }
    }
  };

  const performMove = async ({ project, sourceColumn, destColumn, sourceIndex, destinationIndex }) => {
    if (!project) return;

    const newData = {
      pending: [...(kanbanData.pending || [])],
      progress: [...(kanbanData.progress || [])],
      completed: [...(kanbanData.completed || [])]
    };

    if (!Array.isArray(newData[sourceColumn]) || !Array.isArray(newData[destColumn])) {
      console.error('Columnas inválidas para mover el proyecto:', sourceColumn, destColumn);
      return;
    }

    const [removedItem] = newData[sourceColumn].splice(sourceIndex, 1);
    if (!removedItem) {
      console.error('No se encontró el proyecto a mover en la columna origen');
      return;
    }

    const updatedItem = {
      ...removedItem,
      status: destColumn
    };

    let targetIndex = destinationIndex;
    if (sourceColumn === destColumn && destinationIndex > sourceIndex) {
      targetIndex = destinationIndex - 1;
    }

    newData[destColumn].splice(targetIndex, 0, updatedItem);
    setKanbanData(newData);

    if (sourceColumn !== destColumn) {
      const loadingToast = toast.loading('Actualizando proyecto...');

      try {
        const result = await ProjectService.updateProject(project.id, {
          name: project.name,
          description: project.description,
          status: destColumn,
          initialDate: project.initialDate,
          finalDate: project.finalDate,
          progress: project.progress
        });

        if (result.success) {
          toast.success('Proyecto actualizado exitosamente', { id: loadingToast });
          await loadKanbanData();
          await refreshProjectTasks(project.id);
        } else {
          toast.error('Error al actualizar el proyecto', { id: loadingToast });
          await loadKanbanData();
        }
      } catch (error) {
        console.error('❌ Error al actualizar proyecto:', error);
        toast.error('Error al actualizar el proyecto', { id: loadingToast });
        await loadKanbanData();
      }
    }
  };

  const handleDragEnd = async (result) => {
    console.log('🎯 handleDragEnd llamado:', result);
    
    const { source, destination } = result;

    // Si no hay destino, cancelar
    if (!destination) {
      console.log('❌ No hay destino, cancelando');
      return;
    }

    // Si se soltó en la misma posición, cancelar
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('❌ Misma posición, cancelando');
      return;
    }

    const sourceColumn = source.droppableId;
    const destColumn = destination.droppableId;
    const sourceItems = kanbanData[sourceColumn] || [];
    const movedItem = sourceItems[source.index];

    if (!movedItem) {
      console.error('No se encontró el proyecto para mover');
      return;
    }

    console.log(`📦 Moviendo de ${sourceColumn}[${source.index}] a ${destColumn}[${destination.index}]`);

    if (sourceColumn !== destColumn && destColumn === 'completed') {
      const validationToast = toast.loading('Verificando tareas pendientes...');

      try {
        const tasksResult = await TaskService.getProjectTasks(movedItem.id);

        toast.dismiss(validationToast);

        if (tasksResult.success) {
          const incompleteTasks = (tasksResult.tasks || []).filter(
            (task) => (task.status || '').toLowerCase() !== 'completed'
          );

          if (incompleteTasks.length > 0) {
            setCompletionModal({
              isOpen: true,
              project: movedItem,
              sourceColumn,
              destColumn,
              sourceIndex: source.index,
              destinationIndex: destination.index,
              incompleteTasksCount: incompleteTasks.length
            });
            return;
          }
        } else {
          toast.error('No se pudieron verificar las tareas del proyecto');
        }
      } catch (error) {
        toast.dismiss(validationToast);
        console.error('Error al verificar tareas del proyecto:', error);
        toast.error('No se pudieron verificar las tareas del proyecto');
      }

      await performMove({
        project: movedItem,
        sourceColumn,
        destColumn,
        sourceIndex: source.index,
        destinationIndex: destination.index
      });
      return;
    }

    await performMove({
      project: movedItem,
      sourceColumn,
      destColumn,
      sourceIndex: source.index,
      destinationIndex: destination.index
    });
  };

  const columns = useMemo(() => [
    {
      id: 'pending',
      title: 'Pendiente',
      icon: ClockIcon,
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
      headerBg: 'bg-blue-100 dark:bg-blue-900/40',
      data: kanbanData.pending || []
    },
    {
      id: 'progress',
      title: 'En Progreso',
      icon: ExclamationCircleIcon,
      color: 'yellow',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      headerBg: 'bg-yellow-100 dark:bg-yellow-900/40',
      data: kanbanData.progress || []
    },
    {
      id: 'completed',
      title: 'Completado',
      icon: CheckCircleIcon,
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
      headerBg: 'bg-green-100 dark:bg-green-900/40',
      data: kanbanData.completed || []
    }
  ], [kanbanData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const ProjectCard = ({ project, index }) => {
    // Colores de fondo para el icono
    const iconBgColor = project.status === 'completed' ? 'bg-green-100 dark:bg-green-900' :
                       project.status === 'progress' ? 'bg-yellow-100 dark:bg-yellow-900' :
                       'bg-blue-100 dark:bg-blue-900';
    
    // Color de la barra de progreso
    const progressColor = project.status === 'completed' ? 'bg-green-500' :
                         project.status === 'progress' ? 'bg-yellow-500' :
                         'bg-blue-500';

    return (
      <Draggable 
        draggableId={`project-${project.id}`} 
        index={index}
        type="PROJECT"
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={() => handleProjectClick(project)}
            className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-200 dark:border-gray-700 relative ${
              snapshot.isDragging ? 'shadow-2xl ring-2 ring-primary-500 rotate-2 cursor-grabbing' : ''
            }`}
            style={{
              ...provided.draggableProps.style,
            }}
          >
        {/* Header con icono, título y botones */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <div className={`w-10 h-10 ${iconBgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <span className="text-xl">{project.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight mb-1">
                {project.name}
              </h4>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColorClasses(project.status)}`}>
                {getStatusLabel(project.status)}
              </span>
            </div>
          </div>
          {/* Botones de acción */}
          <div className="flex items-center space-x-1 ml-2">
            <button
              onClick={(e) => handleEditProject(project, e)}
              className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-600 transition-colors shadow-sm"
              title="Editar proyecto"
            >
              <PencilIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" />
            </button>
            <button
              onClick={(e) => handleDeleteProject(project, e)}
              className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-600 transition-colors shadow-sm"
              title="Eliminar proyecto"
            >
              <TrashIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-300" />
            </button>
          </div>
        </div>

        {/* Descripción */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
          {project.description}
        </p>

        {/* Etiquetas */}
        {project.tags && project.tags.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {project.tags.map(tag => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                style={getTagChipStyles(tag.color)}
                title={tag.name}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Barra de progreso */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Progreso</span>
            <span className="font-medium">{project.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`${progressColor} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>

            {/* Participantes */}
            {project.participants && project.participants.length > 0 && (
              <div className="mb-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 mb-2">
                  <UserGroupIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Participantes ({project.participants.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.participants.map((participant) => (
                    <div
                      key={participant.id}
                      className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                      title={participant.email}
                    >
                      <span className="font-medium">
                        {participant.name} {participant.last_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer con info adicional */}
            <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-1">
                <ListBulletIcon className="h-4 w-4" />
                <span>{project.tasksCount} tareas</span>
              </div>
              <div className="flex items-center space-x-1">
                <CalendarIcon className="h-4 w-4" />
                <span>{formatDate(project.finalDate)}</span>
              </div>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Tablero Kanban
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestiona tus proyectos de forma visual
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nuevo Proyecto</span>
          </button>
          <button
            onClick={loadKanbanData}
            className="btn-secondary flex items-center space-x-2"
            disabled={loading}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{loading ? 'Cargando...' : 'Actualizar'}</span>
          </button>
        </div>
      </div>

      {/* Tablero Kanban - SIEMPRE renderizado */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {columns.map((column) => (
            <div key={column.id} className="flex flex-col">
              {/* Header de la columna */}
              <div className={`${column.headerBg} ${column.borderColor} border-2 rounded-t-lg p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <column.icon className={`h-5 w-5 text-${column.color}-600`} />
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {column.title}
                    </h3>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${column.color}-200 dark:bg-${column.color}-800 text-${column.color}-800 dark:text-${column.color}-200`}>
                    {column.data.length}
                  </span>
                </div>
              </div>

              {/* Contenedor de tarjetas - SIEMPRE renderizado */}
              <Droppable droppableId={column.id} type="PROJECT">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`${column.bgColor} ${column.borderColor} border-2 border-t-0 rounded-b-lg p-4 min-h-[500px] max-h-[calc(100vh-300px)] flex-1 transition-colors overflow-y-auto kanban-scrollbar ${
                      snapshot.isDraggingOver ? 'ring-2 ring-primary-400 bg-primary-50 dark:bg-primary-900/30' : ''
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                      </div>
                    ) : (
                      <div className="space-y-3 pb-2">
                        {column.data.length > 0 ? (
                          column.data.map((project, index) => (
                            <ProjectCard key={project.id} project={project} index={index} />
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                            <p className="text-sm">No hay proyectos</p>
                            {snapshot.isDraggingOver && (
                              <p className="text-xs mt-2">Suelta aquí</p>
                            )}
                          </div>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* Stats footer */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {kanbanData.pending.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Proyectos Pendientes
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
              {kanbanData.progress.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Proyectos en Progreso
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {kanbanData.completed.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Proyectos Completados
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Creación de Proyecto */}
      <CreateProjectModal 
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* Modal de Edición de Proyecto */}
      {selectedProject && (
        <EditProjectModal 
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {/* Modal de Tareas del Proyecto */}
      {selectedProject && (
        <ProjectTasksModal 
          isOpen={isTasksModalOpen}
          onClose={() => {
            setIsTasksModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onTasksUpdated={loadKanbanData}
          refreshSignal={tasksRefreshSignal}
        />
      )}

      <AnimatePresence>
        {completionModal.isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={() => {
                if (!completionProcessing) {
                  resetCompletionModal();
                }
              }}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="card w-full max-w-md p-6"
              >
                <div className="text-3xl mb-4 text-amber-500 text-center">⚠️</div>
                <p className="text-base text-gray-900 dark:text-white font-medium mb-4 text-center">
                  "Este cambio cerrará automáticamente todas las tareas no completadas del proyecto. ¿Deseas continuar?"
                </p>
                {completionModal.incompleteTasksCount > 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-300 mb-6">
                    Tareas pendientes detectadas: {completionModal.incompleteTasksCount}
                  </p>
                )}
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetCompletionModal}
                    disabled={completionProcessing}
                    className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (completionProcessing || !completionModal.project) return;
                      setCompletionProcessing(true);
                      await performMove({
                        project: completionModal.project,
                        sourceColumn: completionModal.sourceColumn,
                        destColumn: completionModal.destColumn,
                        sourceIndex: completionModal.sourceIndex,
                        destinationIndex: completionModal.destinationIndex
                      });
                      resetCompletionModal();
                    }}
                    disabled={completionProcessing}
                    className="px-4 py-2 rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {completionProcessing ? 'Actualizando...' : 'Aceptar'}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Modal de Confirmación de Eliminación */}
      <AnimatePresence>
        {deleteModal.isOpen && deleteModal.project && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={handleCancelDelete}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="card w-full max-w-md p-6 relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header del modal */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                      <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Eliminar Proyecto
                    </h3>
                  </div>
                  {!deleting && (
                    <button
                      onClick={handleCancelDelete}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>

                {/* Contenido del modal */}
                <div className="mb-6">
                  <p className="text-base text-gray-700 dark:text-gray-300 mb-4">
                    ¿Estás seguro de que deseas eliminar el proyecto <span className="font-semibold text-gray-900 dark:text-white">"{deleteModal.project.name}"</span>?
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      ⚠️ Esta acción no se puede deshacer y eliminará todas las tareas asociadas al proyecto.
                    </p>
                  </div>
                </div>

                {/* Indicador de carga */}
                {deleting && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center space-x-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                          Eliminando proyecto...
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Esto puede tomar unos segundos. Por favor espera.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botones */}
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    disabled={deleting}
                    className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {deleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KanbanView;

